// Pure date-range helpers used by ReportsPage. Extracted so they can
// be unit-tested without rendering React components.
//
// Three concepts:
//   1. A RANGE is a UI-visible label like 'mtd' / 'qtd' / 'last 30
//      days' / 'custom'. UI-facing, not exported here — see ReportsPage.
//   2. A WINDOW is a {start, end} timestamp pair. Internal data-
//      filtering primitive. Built from a range via rangeWindow().
//   3. A PRIOR window is computed from a current window with
//      priorWindow() — same duration, immediately preceding.
//
// Why end is end-of-day (23:59:59.999):
//   Time entries / invoices include rows whose date field is "today"
//   — using end-of-day means today's data is included in the current
//   window, which matches what users expect when they read "month to
//   date" or "last 30 days."

export function rangeStart(range) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  switch (range) {
    case 'mtd': d.setDate(1); return d;
    case 'qtd': {
      const q = Math.floor(d.getMonth() / 3) * 3;
      return new Date(d.getFullYear(), q, 1);
    }
    case 'ytd':  return new Date(d.getFullYear(), 0, 1);
    case '30':   d.setDate(d.getDate() - 30); return d;
    case '90':   d.setDate(d.getDate() - 90); return d;
    case '365':  d.setDate(d.getDate() - 365); return d;
    case 'all':  return new Date(0);
    default:     return new Date(0);
  }
}

// Returns {start, end}. For custom ranges, accepts ISO date strings
// (YYYY-MM-DD) and returns sensible defaults for empty inputs so the
// page never breaks on partial user input.
export function rangeWindow(range, customStart, customEnd) {
  const now = new Date();
  now.setHours(23, 59, 59, 999);
  if (range === 'custom') {
    const start = customStart
      ? new Date(`${customStart}T00:00:00`)
      : (() => {
          const d = new Date();
          d.setFullYear(d.getFullYear() - 1);
          d.setHours(0, 0, 0, 0);
          return d;
        })();
    const end = customEnd ? new Date(`${customEnd}T23:59:59.999`) : now;
    return { start, end };
  }
  return { start: rangeStart(range), end: now };
}

// Same DURATION as the current window, ending 1 ms before the current
// window starts. For 'last 30 days' the prior window is "the 30 days
// before that." For 'mtd' on the 15th of the month, the prior window
// is the first 15 days of the previous month. Etc.
export function priorWindow({ start, end }) {
  const ms = end.getTime() - start.getTime();
  return {
    start: new Date(start.getTime() - ms - 1),
    end:   new Date(start.getTime() - 1),
  };
}

// Percent change for KPI deltas. Returns null when the prior value
// is 0 or non-finite — division by zero would be infinite (or NaN)
// and the UI hides null deltas. So "showed 0 last month, showed
// something this month" surfaces as no delta arrow rather than as
// a misleading "+∞%."
export function pct(curr, prior) {
  if (!Number.isFinite(prior) || prior === 0) return null;
  return Math.round(((curr - prior) / prior) * 100);
}
